import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Gift, Mail, Sparkles, Check, Heart, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { generateCertificateCode, formatCertDate } from '../utils/giftCertificatePDF';

const PACKAGES = [
  {
    id: '30-min',
    label: '30-Minute Massage',
    price: 55,
    tagline: 'A focused reset',
    description: 'Targeted work on the neck, shoulders, or one problem area. Great for a first-timer, a lunch break, or a short recovery session.',
    features: ['Personalized intake', 'Focus on one area', 'Warm, professional space'],
    accent: 'from-rose-50 to-white',
    ring: 'ring-rose-100',
  },
  {
    id: '60-min',
    label: '60-Minute Massage',
    price: 110,
    tagline: 'The classic hour',
    popular: true,
    description: 'A full-body session with time to address everything meaningful — the most-gifted option and the one most people book for themselves.',
    features: ['Full-body session', 'Swedish or deep tissue', 'Time to actually settle in'],
    accent: 'from-teal-50 to-white',
    ring: 'ring-teal-200',
  },
  {
    id: '90-min',
    label: '90-Minute Massage',
    price: 160,
    tagline: 'A proper reset',
    description: 'The gift for someone carrying a lot. Deep, unhurried work with time for the shoulders, low back, hips, and everything else that\'s been asking for attention.',
    features: ['Extended full-body session', 'Deeper, unhurried pace', 'Room for problem areas'],
    accent: 'from-amber-50 to-white',
    ring: 'ring-amber-100',
  },
];

const GiftCertificatesPage = () => {
  const { toast } = useToast();
  const [selectedPackageId, setSelectedPackageId] = useState('60-min');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lastCert, setLastCert] = useState(null);
  const [formData, setFormData] = useState({
    buyerFirstName: '',
    buyerLastName: '',
    buyerEmail: '',
    buyerPhone: '',
    recipientFirstName: '',
    recipientEmail: '',
    deliveryMethod: 'email',
    occasion: '',
    personalMessage: '',
    paymentPreference: 'venmo',
  });

  useEffect(() => {
    const prev = document.title;
    document.title = 'Gift Certificates — Massage Therapy Gift Cards | Ayumi Suzuki';
    return () => {
      document.title = prev;
    };
  }, []);

  const selectedPackage = PACKAGES.find((p) => p.id === selectedPackageId);
  const isoDate = '2026-02-14';
  const canonical = 'https://ayumisuzuki.com/gift-certificates';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Prepare certificate metadata (code + preview URL) so Ayumi has a one-click link
    const certCode = generateCertificateCode();
    const sessionKey = selectedPackage.id.split('-')[0]; // '30' | '60' | '90'
    const issued = new Date();
    const validThrough = new Date(issued);
    validThrough.setFullYear(validThrough.getFullYear() + 1);

    const previewParams = new URLSearchParams({
      r: formData.recipientFirstName,
      t: sessionKey,
      m: formData.personalMessage || '',
      o: formData.occasion || '',
      b: `${formData.buyerFirstName} ${formData.buyerLastName}`.trim(),
      code: certCode,
    });
    const previewUrl = `${window.location.origin}/gift-certificate-preview?${previewParams.toString()}`;

    try {
      const response = await fetch('https://formspree.io/f/maqkqowk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: `Gift Certificate Request — ${selectedPackage.label} ($${selectedPackage.price}) — ${certCode}`,
          formType: 'Gift Certificate Request',
          package: `${selectedPackage.label} — $${selectedPackage.price}`,
          buyerName: `${formData.buyerFirstName} ${formData.buyerLastName}`,
          buyerEmail: formData.buyerEmail,
          buyerPhone: formData.buyerPhone,
          recipientName: formData.recipientFirstName,
          recipientEmail: formData.recipientEmail,
          deliveryMethod: formData.deliveryMethod,
          occasion: formData.occasion || 'Not specified',
          personalMessage: formData.personalMessage || 'None',
          paymentPreference: formData.paymentPreference,
          certificateCode: certCode,
          certificatePreviewUrl: previewUrl,
          instructionsForAyumi:
            `PAYMENT-FIRST WORKFLOW — Do NOT download or send the certificate yet. (1) Reply to the buyer and share payment options. (2) Wait for payment to clear (Venmo/Zelle/in-person card). (3) Only then open the preview URL above, click "Download PDF certificate," and email the signed PDF to the buyer or recipient. Certificate valid through ${formatCertDate(validThrough)}. Record code ${certCode} in your paid-certificates list once payment clears.`,
        }),
      });

      if (response.ok) {
        setLastCert({
          recipientName: formData.recipientFirstName || 'Valued Recipient',
          sessionLength: selectedPackage.label.replace(' Massage', ''),
          value: selectedPackage.price,
          message: formData.personalMessage || '',
          occasion: formData.occasion || '',
          code: certCode,
          issuedOn: formatCertDate(issued),
          validThrough: formatCertDate(validThrough),
          buyerName: `${formData.buyerFirstName} ${formData.buyerLastName}`.trim(),
        });
        setSubmitted(true);
        toast({
          title: 'Request sent!',
          description: 'Ayumi will email you within 24 hours to coordinate the gift certificate.',
        });
        setFormData({
          buyerFirstName: '',
          buyerLastName: '',
          buyerEmail: '',
          buyerPhone: '',
          recipientFirstName: '',
          recipientEmail: '',
          deliveryMethod: 'email',
          occasion: '',
          personalMessage: '',
          paymentPreference: 'venmo',
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again, or email ayumisuzuki@yahoo.com directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-rose-50" data-testid="gift-certificates-page">
      {/* SEO / AEO schema — offered Products */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: PACKAGES.map((pkg, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              item: {
                '@type': 'Product',
                name: `${pkg.label} Gift Certificate — Ayumi Suzuki Massage`,
                description: pkg.description,
                category: 'Massage Therapy Gift Certificate',
                brand: { '@type': 'Brand', name: 'Ayumi Suzuki Massage Therapy' },
                offers: {
                  '@type': 'Offer',
                  price: pkg.price,
                  priceCurrency: 'USD',
                  availability: 'https://schema.org/InStock',
                  url: canonical,
                  seller: { '@type': 'Organization', name: 'Ayumi Suzuki Massage Therapy' },
                },
              },
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': canonical,
            name: 'Massage Gift Certificates in Sacramento — Ayumi Suzuki',
            description:
              'Give the gift of a professional massage in midtown Sacramento. 30-, 60-, and 90-minute gift certificates coordinated by hand with certified therapist Ayumi Suzuki.',
            dateModified: isoDate,
            inLanguage: 'en-US',
          }),
        }}
      />

      {/* Simple top bar */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link
            to="/"
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors duration-300 group"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span>Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
            <Gift className="w-4 h-4" /> Gift Certificates
          </div>
          <h1 className="text-4xl md:text-6xl font-serif text-gray-800 mb-6 leading-tight" data-testid="gift-hero-title">
            Give someone an hour of quiet.
          </h1>
          <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
            Massage gift certificates from Ayumi Suzuki's midtown Sacramento practice. Coordinated by hand — no automated system, no generic printouts. Pick a session length, tell me who it's for, and I'll take care of the rest.
          </p>
        </div>
      </section>

      {/* Packages */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="gift-packages-section">
        <div className="grid md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg) => {
            const isSelected = selectedPackageId === pkg.id;
            return (
              <button
                key={pkg.id}
                onClick={() => setSelectedPackageId(pkg.id)}
                className={`text-left bg-gradient-to-br ${pkg.accent} rounded-3xl p-8 border shadow-sm hover:shadow-xl transition-all duration-500 relative ${
                  isSelected ? `ring-4 ${pkg.ring} border-teal-400 -translate-y-1` : 'border-gray-100 hover:-translate-y-1'
                }`}
                data-testid={`gift-package-${pkg.id}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-8 bg-teal-600 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">
                    Most Gifted
                  </div>
                )}
                {isSelected && (
                  <div className="absolute top-4 right-4 bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 mb-3">
                  <Clock className="w-3.5 h-3.5" />
                  {pkg.tagline}
                </div>
                <h3 className="text-2xl font-serif text-gray-800 mb-2">{pkg.label}</h3>
                <div className="text-4xl font-serif text-teal-700 mb-4">
                  ${pkg.price}
                  <span className="text-base text-gray-500 font-sans ml-2">gift value</span>
                </div>
                <p className="text-sm text-gray-600 mb-6 leading-relaxed">{pkg.description}</p>
                <ul className="space-y-2 mb-4">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div
                  className={`text-center py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSelected ? 'bg-teal-600 text-white' : 'bg-white text-teal-700 border border-teal-200'
                  }`}
                >
                  {isSelected ? 'Selected' : 'Choose this gift'}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-center text-sm text-gray-500 mt-6" data-testid="custom-amount-note">
          Need a different amount, a couples package, or a corporate bulk order? Just add it in the message below.
        </p>
      </section>

      {/* How it works */}
      <section className="bg-white py-16" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-4">How it works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Every gift certificate is coordinated by Ayumi personally — no shopping cart, no automated PDF. Payment first, certificate second.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center" data-testid="step-1">
              <div className="w-14 h-14 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-serif">1</div>
              <h3 className="text-lg font-serif text-gray-800 mb-2">You send the request</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Fill out the short form with the package, the recipient, and any personal note.
              </p>
            </div>
            <div className="text-center" data-testid="step-2">
              <div className="w-14 h-14 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-serif">2</div>
              <h3 className="text-lg font-serif text-gray-800 mb-2">Ayumi emails you</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Within 24 hours, she confirms details and shares payment options — Venmo, Zelle, or in-person card.
              </p>
            </div>
            <div className="text-center" data-testid="step-3">
              <div className="w-14 h-14 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-serif">3</div>
              <h3 className="text-lg font-serif text-gray-800 mb-2">You send payment</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Once the payment clears on Ayumi's end, the certificate is officially issued to your reference number.
              </p>
            </div>
            <div className="text-center" data-testid="step-4">
              <div className="w-14 h-14 bg-teal-100 text-teal-700 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-serif">4</div>
              <h3 className="text-lg font-serif text-gray-800 mb-2">Ayumi sends the PDF</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                A signed, branded certificate is emailed to you or the recipient — ready to print or forward.
              </p>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-8 max-w-2xl mx-auto">
            Certificates are only valid once payment is confirmed and Ayumi has issued the code. No PDF is sent before that.
          </p>
        </div>
      </section>

      {/* Coordination Form */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" id="request-form" data-testid="gift-form-section">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <Mail className="w-6 h-6 text-teal-600" />
              <span className="text-xs font-semibold uppercase tracking-wider text-teal-700">Coordinate by email</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-3">Request your gift certificate</h2>
            <p className="text-gray-600">
              You've selected the <strong className="text-teal-700">{selectedPackage.label}</strong> at <strong>${selectedPackage.price}</strong>. Change it any time by tapping a card above.
            </p>
          </div>

          {submitted ? (
            <div className="text-center py-12" data-testid="success-message">
              <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-teal-600" />
              </div>
              <h3 className="text-2xl font-serif text-gray-800 mb-3">Your request is on its way to Ayumi.</h3>
              <p className="text-gray-600 max-w-lg mx-auto mb-6">
                She'll reach out within 24 hours to confirm details and share payment options. Keep an eye on your inbox.
              </p>

              {lastCert && (
                <div className="max-w-md mx-auto bg-teal-50 border border-teal-100 rounded-2xl p-5 mb-6" data-testid="cert-code-block">
                  <p className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-1">Your reference number</p>
                  <p className="text-xl font-mono text-gray-800 tracking-wider">{lastCert.code}</p>
                  <p className="text-xs text-gray-500 mt-2">Ayumi has this on her end too — no need to write it down.</p>
                </div>
              )}

              {/* Next-steps timeline — payment first, certificate second */}
              <div className="max-w-xl mx-auto text-left bg-white border border-gray-100 rounded-2xl p-6 mb-6 shadow-sm" data-testid="next-steps-block">
                <p className="text-xs uppercase tracking-widest text-teal-700 font-semibold mb-3 text-center">What happens next</p>
                <ol className="space-y-3 text-sm text-gray-700">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center font-semibold">1</span>
                    <span>Ayumi emails you within 24 hours to confirm details.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center font-semibold">2</span>
                    <span>You send payment (Venmo, Zelle, or in-person card).</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-teal-600 text-white text-xs flex items-center justify-center font-semibold">3</span>
                    <span>Once payment clears, Ayumi emails the signed, branded PDF certificate to you or the recipient.</span>
                  </li>
                </ol>
                <p className="text-xs text-gray-500 mt-4 text-center italic">
                  Certificates are issued only after payment is confirmed — that's why nothing downloads today.
                </p>
              </div>

              <button
                onClick={() => setSubmitted(false)}
                className="text-teal-700 hover:text-teal-800 font-medium underline underline-offset-4"
                data-testid="send-another-btn"
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="gift-form">
              {/* Buyer info */}
              <div>
                <h3 className="text-lg font-serif text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-semibold">1</span>
                  About you
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Your first name *</label>
                    <input
                      type="text"
                      name="buyerFirstName"
                      value={formData.buyerFirstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="buyer-first-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Your last name *</label>
                    <input
                      type="text"
                      name="buyerLastName"
                      value={formData.buyerLastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="buyer-last-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Your email *</label>
                    <input
                      type="email"
                      name="buyerEmail"
                      value={formData.buyerEmail}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="buyer-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Your phone (optional)</label>
                    <input
                      type="tel"
                      name="buyerPhone"
                      value={formData.buyerPhone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="buyer-phone-input"
                    />
                  </div>
                </div>
              </div>

              {/* Recipient info */}
              <div className="pt-4">
                <h3 className="text-lg font-serif text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-semibold">2</span>
                  About the recipient
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Recipient first name *</label>
                    <input
                      type="text"
                      name="recipientFirstName"
                      value={formData.recipientFirstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="recipient-first-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Recipient email (optional)</label>
                    <input
                      type="email"
                      name="recipientEmail"
                      value={formData.recipientEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="recipient-email-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank if you'd like to give the gift in person.</p>
                  </div>
                </div>
              </div>

              {/* Preferences */}
              <div className="pt-4">
                <h3 className="text-lg font-serif text-gray-800 mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs flex items-center justify-center font-semibold">3</span>
                  How it should arrive
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Delivery method</label>
                    <select
                      name="deliveryMethod"
                      value={formData.deliveryMethod}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all bg-white"
                      data-testid="delivery-method-select"
                    >
                      <option value="email">Email to the recipient</option>
                      <option value="email-to-me">Email to me (I'll forward it)</option>
                      <option value="print">Printed certificate (pick up in midtown)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Preferred payment</label>
                    <select
                      name="paymentPreference"
                      value={formData.paymentPreference}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all bg-white"
                      data-testid="payment-preference-select"
                    >
                      <option value="venmo">Venmo</option>
                      <option value="zelle">Zelle</option>
                      <option value="card-in-person">Card (in person)</option>
                      <option value="other">Other — I'll explain in the message</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Occasion + message */}
              <div className="pt-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">Occasion (optional)</label>
                    <input
                      type="text"
                      name="occasion"
                      value={formData.occasion}
                      onChange={handleChange}
                      placeholder="Birthday, thank you, holiday, just because…"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="occasion-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">When should it be delivered?</label>
                    <input
                      type="text"
                      name="deliveryDate"
                      placeholder="ASAP, or a specific date"
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all"
                      data-testid="delivery-date-input"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm text-gray-700 mb-1">Personal message on the certificate</label>
                  <textarea
                    name="personalMessage"
                    value={formData.personalMessage}
                    onChange={handleChange}
                    rows="4"
                    placeholder="A short note to include on the certificate — a few words, or a whole letter."
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:border-teal-500 focus:ring-2 focus:ring-teal-100 focus:outline-none transition-all resize-none"
                    data-testid="personal-message-textarea"
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="bg-teal-50 border border-teal-100 rounded-2xl p-6" data-testid="order-summary">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm uppercase tracking-wider text-teal-700 font-semibold">Your gift</span>
                  <Heart className="w-4 h-4 text-teal-600" />
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-lg font-serif text-gray-800">{selectedPackage.label}</span>
                  <span className="text-2xl font-serif text-teal-700">${selectedPackage.price}</span>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Ayumi will confirm this by email within 24 hours before charging anything. No payment is taken through this form.
                </p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white py-4 rounded-lg font-medium tracking-wide transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-gift-request-btn"
              >
                {isSubmitting ? (
                  'Sending your request…'
                ) : (
                  <>
                    <Gift className="w-5 h-5" />
                    Send request to Ayumi
                  </>
                )}
              </button>
              <p className="text-xs text-center text-gray-500">
                Or email <a href="mailto:ayumisuzuki@yahoo.com" className="text-teal-600 hover:underline">ayumisuzuki@yahoo.com</a> directly · Studio at 2528 I Street, midtown Sacramento
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="bg-white py-16" data-testid="gift-faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-serif text-gray-800 mb-8 text-center">Frequently asked</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-serif text-gray-800 mb-1">When does the certificate actually get sent?</h3>
              <p className="text-sm text-gray-600">After Ayumi confirms your payment has cleared. Nothing is issued or delivered until she has payment in hand.</p>
            </div>
            <div>
              <h3 className="text-lg font-serif text-gray-800 mb-1">Do gift certificates expire?</h3>
              <p className="text-sm text-gray-600">Gift certificates are valid for one year from the date of issue. Extensions are welcome — just email me.</p>
            </div>
            <div>
              <h3 className="text-lg font-serif text-gray-800 mb-1">Can the recipient upgrade to a longer session?</h3>
              <p className="text-sm text-gray-600">Yes — they can apply the certificate value toward any session and pay the difference at check-out.</p>
            </div>
            <div>
              <h3 className="text-lg font-serif text-gray-800 mb-1">Can I buy multiple certificates at once?</h3>
              <p className="text-sm text-gray-600">Absolutely. Add the quantities and any variations in the personal message, and I'll send one confirmation email covering everything.</p>
            </div>
            <div>
              <h3 className="text-lg font-serif text-gray-800 mb-1">Is this refundable?</h3>
              <p className="text-sm text-gray-600">Certificates are non-refundable but fully transferable — the recipient can gift it forward at any time.</p>
            </div>
            <div>
              <h3 className="text-lg font-serif text-gray-800 mb-1">How does the recipient book?</h3>
              <p className="text-sm text-gray-600">Through the regular online booking page or by texting the studio. They mention the certificate at check-out.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <div className="bg-teal-700 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-teal-100">
            Ayumi Suzuki · California CAMTC-certified · 2528 I Street, midtown Sacramento
          </p>
          <div className="mt-3 flex justify-center gap-6 text-sm">
            <Link to="/" className="hover:underline underline-offset-4">Home</Link>
            <Link to="/book" className="hover:underline underline-offset-4">Book a session</Link>
            <Link to="/faq" className="hover:underline underline-offset-4">FAQ</Link>
            <a href="mailto:ayumisuzuki@yahoo.com" className="hover:underline underline-offset-4">Email</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftCertificatesPage;
